// Cascade-badge colour tiers + prior formatting (presentation only). Cascade
// testing badges use amber — the "action needed" register that matches the
// Recommended Actions sidebar cards (#ed6c02) — instead of purple, which
// elsewhere in TrakGene means genome/genotype data. A saturation gradient
// distinguishes prior-probability tiers; the label inside the badge still carries
// the same information, so colour is never the sole signal.
//
// Thresholds are CLINICAL decisions (standard Mendelian inflection points), not
// design choices: 0.5 = first-degree relative of a confirmed carrier; 0.25 =
// conditional on an untested intermediate relative; below that = more distant /
// watch-only. Order matters: an explicit `defer` always wins.

export const PRIMARY_THRESHOLD = 0.5;
export const SECONDARY_THRESHOLD = 0.25;

// Keyed to the Recommended Actions amber (#ed6c02). White text/stroke kept by the
// renderer for legibility on all of them.
export const CASCADE_BADGE_COLORS = Object.freeze({
  primary: '#ed6c02', // priorProbability >= 0.5
  secondary: '#f0a560', // 0.25 <= priorProbability < 0.5
  deferred: '#9a8b7e', // priorProbability < 0.25 OR deferred
  // No probability was stated — an unpriceable inheritance mode, or a result
  // already on file (TGCLOUD-1712 W4.1 / W4.3). Deliberately a NEUTRAL slate
  // rather than a paler amber: this is an absence of a risk figure, not a low
  // one, and it must not read as "nearly zero".
  unpriced: '#6b7a8c'
});

/**
 * True when the candidate is age-deferred under RISK CONTROL RC-PAED-1.
 * Reads the structured `deferral` first, then the legacy `recommendedAction` /
 * `deferReason` — the family view still synthesises `{ recommendedAction:
 * 'defer' }` objects, so that path must keep working.
 */
const isDeferred = (candidate) =>
  !!(
    candidate &&
    (candidate.recommendedAction === 'defer' ||
      (candidate.deferral && candidate.deferral.deferred) ||
      candidate.deferReason === 'minor' ||
      candidate.deferReason === 'age_unknown')
  );

export function getCascadeBadgeTier(candidate) {
  if (!candidate) return 'deferred';
  if (isDeferred(candidate)) return 'deferred';
  // No stated prior → its own tier. Never fall through to a numeric threshold:
  // `priorProbability || 0` would colour an unpriced candidate as if its risk
  // were below 25%.
  if (candidate.priorProbability == null) return 'unpriced';
  const prior = candidate.priorProbability;
  if (prior >= PRIMARY_THRESHOLD) return 'primary';
  if (prior >= SECONDARY_THRESHOLD) return 'secondary';
  return 'deferred';
}

// ---------------------------------------------------------------------------
// Formatting (TGCLOUD-1712 W4.9)
// ---------------------------------------------------------------------------
//
// `Math.round(prior * 100)` rendered 12.5% as "13%" — a value no genetics
// reference produces, on a figure the clinician may quote to a patient. The
// analyzer now emits the EXACT prior and formatting happens here, once.

const MAX_DECIMALS = 1;

/**
 * A prior as an exact percentage string, or null when there is no prior.
 * Trailing zeros are trimmed: 0.5 → "50%", 0.125 → "12.5%", 2/3 → "66.7%".
 * @param {number|null|undefined} prior
 * @param {{ maxDecimals?: number }} [opts]
 * @returns {string|null}
 */
export function formatPriorPercent(prior, opts = {}) {
  if (prior == null || !Number.isFinite(Number(prior))) return null;
  const maxDecimals = Number.isFinite(opts.maxDecimals)
    ? opts.maxDecimals
    : MAX_DECIMALS;
  const pct = Number(prior) * 100;
  // toFixed then trim, so 12.5 keeps its half and 50.0 does not gain one.
  const fixed = pct.toFixed(maxDecimals);
  return `${fixed.replace(/\.?0+$/, '')}%`;
}

/**
 * The short text a badge should carry, or null when the candidate has no prior
 * — in which case the renderer must draw something other than a percentage
 * (the relationship and mode, per DR-4) and must NOT fall back to "0%".
 *
 * Pass `{ prefer: 'fraction' }` for the "1 in 8" form genetics references use;
 * it comes from `derivation.fractionLabel`, which the analyzer emits alongside
 * the exact decimal.
 */
export function cascadeBadgeText(candidate, opts = {}) {
  if (!candidate || candidate.priorProbability == null) return null;
  if (opts.prefer === 'fraction' && candidate.derivation?.fractionLabel)
    return candidate.derivation.fractionLabel;
  return formatPriorPercent(candidate.priorProbability, opts);
}
